// Captacion.jsx - Usando ReclutamientoForm como componente separado
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../AuthContext';
import { useQuery } from '../QueryContext';
import ReclutamientoForm from '../components/ReclutamientoForm';
import './Reclutamiento.css';
import { 
  Search, 
  GraduationCap, 
  BarChart3, 
  Heart,
  Menu,
  X,
  Home,
  Users,
  UserPlus,
  Plus,
  CheckCircle2,
  Clock,
  Filter,
  Download,
  Edit,
  Trash2,
  Loader2,
  AlertTriangle,
  BriefcaseBusiness,
} from 'lucide-react';

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

export default function Captacion() {
  const { hasAccess, profile } = useAuth();
  const { 
    getVacantes, 
    createVacante, 
    updateVacante, 
    deleteVacante,
    getReclutamientoStats
  } = useQuery();
  
  const navigate = useNavigate();

  // Estados de UI
  const [activeTab, setActiveTab] = useState("Captación");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Estados de datos
  const [vacantes, setVacantes] = useState([]);
  const [stats, setStats] = useState({});
  
  // Estados de filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("Todos");
  const [showFilters, setShowFilters] = useState(false);
  
  // Estados de modales
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingVacante, setEditingVacante] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [vacanteToDelete, setVacanteToDelete] = useState(null);
  
  // Estados de UI
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);

  // Tabs de navegación
  const tabs = [
    { label: "Inicio", path: '/talentohumano', icon: Home },
    { label: "Captación", path: '/reclutamiento', icon: Users },
    { label: "Capacitación", path: '/talento-humano/capacitacion', icon: GraduationCap },
    { label: "Evaluaciones", path: '/talento-humano/evaluaciones', icon: BarChart3 },
    { label: "Bienestar", path: '/talento-humano/bienestar', icon: Heart },
  ];

  const visibleTabs = tabs.filter(tab => {
    if (tab.label === "Inicio" || tab.label === "Captación") return true;
    return hasAccess(tab.path);
  });

  const handleTabClick = (tab) => {
    setActiveTab(tab.label);
    navigate(tab.path);
    setMobileMenuOpen(false);
  };

  // Cargar datos
  const fetchVacantes = useCallback(async () => {
    try {
      setLoadingData(true);
      const response = await getVacantes({
        search: searchTerm || undefined,
        status: filterStatus !== 'Todos' ? filterStatus : undefined
      });
      
      if (response?.data) {
        setVacantes(response.data);
      }
    } catch (error) {
      console.error('Error fetching vacantes:', error);
    } finally {
      setLoadingData(false);
    }
  }, [getVacantes, searchTerm, filterStatus]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await getReclutamientoStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [getReclutamientoStats]);

  useEffect(() => {
    fetchVacantes();
    fetchStats();
  }, [fetchVacantes, fetchStats]);

  // Manejar creación/edición
  const handleFormSubmit = async (formData) => {
    try {
      if (editingVacante?.id) {
        // Actualizar
        const response = await updateVacante(editingVacante.id, formData);
        if (response?.status === 'success') {
          setShowFormModal(false);
          setEditingVacante(null);
          fetchVacantes();
          fetchStats();
        }
      } else {
        // Crear
        const response = await createVacante({
          ...formData,
          creado_por: profile?.username || profile?.nombre
        });
        if (response?.status === 'success' || response?.id) {
          setShowFormModal(false);
          fetchVacantes();
          fetchStats();
        }
      }
    } catch (error) {
      console.error('Error saving vacante:', error);
      alert('Error al guardar la solicitud');
    }
  };

  const handleDeleteVacante = async () => {
    if (!vacanteToDelete?.id) return;
    
    setSaving(true);
    try {
      const response = await deleteVacante(vacanteToDelete.id);
      if (response?.status === 'success') {
        setShowDeleteConfirm(false);
        setVacanteToDelete(null);
        fetchVacantes();
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting vacante:', error);
      alert('Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  // Helpers
  const getStatusColor = (status) => {
    switch(status) {
      case 'En Proceso': return '#00ba52';
      case 'Cerrada': return '#06b6d4';
      case 'Cancelada': return '#ef4444';
      case 'Pausada': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString + 'T12:00:00').toLocaleDateString('es-VE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getEstadoAprobacionColor = (estado) => {
    switch(estado) {
      case 'Aprobada': return '#00ba52';
      case 'Pendiente': return '#f59e0b';
      case 'Rechazada': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      transition={isMobile ? { duration: 0.3 } : { duration: 0.6 }}
      className="cap-container"
    >
      {/* HEADER */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.3, delay: 0.1 }}
        className="cap-subheader"
      >
        <div className="cap-subheader-inner">
          <div className="cap-breadcrumb cap-breadcrumb-desktop">
            <span>Portal</span>
            <span className="cap-breadcrumb-separator">›</span>
            <span>Talento Humano</span>
            <span className="cap-breadcrumb-separator">›</span>
            <span className="cap-breadcrumb-current">Captación</span>
          </div>

          <div className="cap-subheader-row">
            <div className="cap-module-label">
              <div className="cap-logo">TH</div>
              <span className="cap-module-title">Talento Humano</span>
              
              <button 
                className="cap-mobile-menu-btn"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              
              <div className="cap-divider-vertical cap-divider-desktop" />
              
              <div className="cap-nav-tabs cap-nav-tabs-desktop">
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.label}
                    onClick={() => handleTabClick(tab)}
                    className={`cap-tab ${activeTab === tab.label ? 'active' : ''}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="cap-mobile-menu"
            >
              <div className="cap-mobile-menu-inner">
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.label}
                    onClick={() => handleTabClick(tab)}
                    className={`cap-mobile-menu-item ${activeTab === tab.label ? 'active' : ''}`}
                  >
                    <tab.icon size={20} />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* MAIN CONTENT */}
      <div className="cap-main-content">
        {/* BANNER */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.4, delay: 0.15 }}
          className="cap-banner"
        >
          <div className="cap-banner-content">
            <div className="cap-banner-text">
              <h1 className="cap-banner-title">Captación de Talento</h1>
              <p className="cap-banner-subtitle">
                Seguimiento completo del proceso de reclutamiento y selección
              </p>
              <div className="cap-banner-buttons">
                <button 
                  className="cap-btn-primary"
                  onClick={() => {
                    setEditingVacante(null);
                    setShowFormModal(true);
                  }}
                >
                  <UserPlus size={18} />
                  Nueva Solicitud
                </button>
                <button 
                  className="cap-btn-secondary"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter size={18} />
                  Filtros
                </button>
              </div>
            </div>
            <div className="cap-banner-stats">
              <div className="cap-stat-card">
                <BriefcaseBusiness size={24} />
                <div className="cap-stat-info">
                  <span className="cap-stat-value">{stats.total_vacantes || 0}</span>
                  <span className="cap-stat-label">Total Vacantes</span>
                </div>
              </div>
              <div className="cap-stat-card">
                <Clock size={24} />
                <div className="cap-stat-info">
                  <span className="cap-stat-value">{stats.en_proceso || 0}</span>
                  <span className="cap-stat-label">En Proceso</span>
                </div>
              </div>
              <div className="cap-stat-card">
                <CheckCircle2 size={24} />
                <div className="cap-stat-info">
                  <span className="cap-stat-value">{stats.cumplen_meta || 0}</span>
                  <span className="cap-stat-label">Cumplen Meta</span>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* FILTROS */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="cap-filters"
            >
              <div className="cap-filter-group">
                <label className="cap-filter-label">Buscar</label>
                <div className="cap-search-input">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="Buscar por cargo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="cap-filter-group">
                <label className="cap-filter-label">Estado de Aprobación</label>
                <select 
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="cap-filter-select"
                >
                  <option value="Todos">Todos</option>
                  <option value="Pendiente">Pendiente</option>
                  <option value="Aprobada">Aprobada</option>
                  <option value="Rechazada">Rechazada</option>
                </select>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* TABLA */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.4, delay: 0.25 }}
          className="cap-table-section"
        >
          <div className="cap-table-header">
            <h2 className="cap-table-title">Solicitudes de Vacantes</h2>
            <div className="cap-table-actions">
              <button className="cap-export-btn">
                <Download size={18} />
                Exportar
              </button>
              <button 
                className="cap-filter-btn"
                onClick={() => {
                  setEditingVacante(null);
                  setShowFormModal(true);
                }}
              >
                <Plus size={18} />
                Nueva
              </button>
            </div>
          </div>

          {loadingData ? (
            <div className="cap-loading">
              <Loader2 size={32} className="cap-spinner" />
              <span>Cargando solicitudes...</span>
            </div>
          ) : (
            <div className="cap-table-wrapper">
              <table className="cap-table">
                <thead>
                  <tr>
                    <th>Cargo</th>
                    <th>Tipo</th>
                    <th>Departamento</th>
                    <th>Ubicación</th>
                    <th>Región</th>
                    <th>Status</th>
                    <th>Motivo</th>
                    <th>Tipo Vacante</th>
                    <th>Solicitante</th>
                    <th>Aprobador</th>
                    <th>Estado Aprobación</th>
                    <th>Fecha Creación</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {vacantes.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="cap-empty-state">
                        No hay solicitudes registradas
                      </td>
                    </tr>
                  ) : (
                    vacantes.map((vacante) => (
                      <tr key={vacante.id} className="cap-table-row">
                        <td className="cap-cargo-cell">{vacante.cargo}</td>
                        <td>
                          <span className="cap-tipo-solicitud-badge">
                            {vacante.tipo_solicitud || 'Nuevo'}
                          </span>
                        </td>
                        <td>{vacante.departamento}</td>
                        <td>{vacante.ubicacion}</td>
                        <td>{vacante.region}</td>
                        <td>
                          <span 
                            className="cap-estado-badge"
                            style={{
                              backgroundColor: `${getStatusColor(vacante.status === 'Vacante' ? 'En Proceso' : 'Cerrada')}22`,
                              color: getStatusColor(vacante.status === 'Vacante' ? 'En Proceso' : 'Cerrada'),
                              border: `1px solid ${getStatusColor(vacante.status === 'Vacante' ? 'En Proceso' : 'Cerrada')}44`
                            }}
                          >
                            {vacante.status}
                          </span>
                        </td>
                        <td>{vacante.motivo}</td>
                        <td>{vacante.tipo_vacante}</td>
                        <td>{vacante.solicitante_nombre || '—'}</td>
                        <td>{vacante.lider_nombre || '—'}</td>
                        <td>
                          <span 
                            className="cap-estado-badge"
                            style={{
                              backgroundColor: `${getEstadoAprobacionColor(vacante.estado_aprobacion)}22`,
                              color: getEstadoAprobacionColor(vacante.estado_aprobacion),
                              border: `1px solid ${getEstadoAprobacionColor(vacante.estado_aprobacion)}44`
                            }}
                          >
                            {vacante.estado_aprobacion || 'Pendiente'}
                          </span>
                        </td>
                        <td>{formatDate(vacante.fecha_creacion)}</td>
                        <td>
                          <div className="cap-actions-cell">
                            <button 
                              className="cap-action-btn"
                              onClick={() => {
                                setEditingVacante(vacante);
                                setShowFormModal(true);
                              }}
                              title="Editar"
                            >
                              <Edit size={14} />
                            </button>
                            <button 
                              className="cap-action-btn cap-action-danger"
                              onClick={() => {
                                setVacanteToDelete(vacante);
                                setShowDeleteConfirm(true);
                              }}
                              title="Eliminar"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </motion.section>
      </div>

      {/* MODAL DE FORMULARIO */}
      <ReclutamientoForm 
        isOpen={showFormModal}
        onClose={() => {
          setShowFormModal(false);
          setEditingVacante(null);
        }}
        onSubmit={handleFormSubmit}
        editData={editingVacante}
        isEditing={!!editingVacante}
      />

      {/* MODAL DE CONFIRMACIÓN */}
      <AnimatePresence>
        {showDeleteConfirm && vacanteToDelete && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="cap-modal-overlay"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div 
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.9 }}
              className="cap-modal cap-modal-confirm"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="cap-modal-header">
                <div className="cap-modal-title">
                  <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                  <div>
                    <h3>Confirmar Eliminación</h3>
                    <p>¿Estás seguro de eliminar esta solicitud?</p>
                  </div>
                </div>
                <button 
                  className="cap-modal-close"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="cap-modal-body">
                <p className="cap-confirm-text">
                  Se eliminará la solicitud de: <strong>{vacanteToDelete.cargo}</strong>
                </p>
                <p className="cap-confirm-warning">
                  Esta acción no se puede deshacer.
                </p>
              </div>
              
              <div className="cap-modal-footer">
                <button 
                  className="cap-modal-btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancelar
                </button>
                <button 
                  className="cap-modal-btn-danger"
                  onClick={handleDeleteVacante}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 size={16} className="cap-spinner" />
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}